import React, { useMemo, useState } from 'react';
import Modal from '../Modal';
import { Button } from '@mes-amis/galette-design-system';
import { Document } from '@/db/types/document';
import { discardLockedOnlineConflict } from '@/db/services/document_service';
import { lockedOnlineCopy } from '@/lib/locked_online';

interface LockedOnlineConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
  /** Resolves a field slug to its form label for the diff table — pass
   *  buildPrefillFormResolvers(...).resolveLabel. Falls back to the raw slug. */
  resolveLabel?: (slug: string) => string;
  /** Seed a new local draft from the kept local edits (reuses the duplicate machinery). */
  onCreateNewDocument: (document: Document) => void | Promise<void>;
}

interface FieldChange {
  field: string;
  label: string;
  localValue: string;
  onlineValue: string;
}

// A completed_online document holds the authoritative server copy in super_form and the
// unsynced local edits in completed_online_local_super_form. Show the fields that differ
// so the user can decide whether to re-create the document from their edits or discard them.
function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (typeof value === 'object') {
    const count = Array.isArray(value) ? value.length : Object.keys(value as object).length;
    return `${count} ${count === 1 ? 'entry' : 'entries'}`;
  }
  return String(value);
}

export default function LockedOnlineConflictModal({
  isOpen,
  onClose,
  document,
  resolveLabel,
  onCreateNewDocument,
}: LockedOnlineConflictModalProps) {
  const [working, setWorking] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  // The modal is only opened for a document flagged completed_online, which the sync layer
  // does only when there ARE differences (a no-diff conflict is adopted silently). So this
  // always has at least one entry — there is no "nothing to resolve" state to handle.
  const changes = useMemo<FieldChange[]>(() => {
    if (!document?.completed_online_local_super_form) return [];
    const local = document.completed_online_local_super_form;
    const online = (document.super_form ?? {}) as Record<string, unknown>;
    return Object.keys(local)
      .filter(field => JSON.stringify(local[field]) !== JSON.stringify(online[field]))
      .map(field => ({
        field,
        label: resolveLabel?.(field) ?? field,
        localValue: displayValue(local[field]),
        onlineValue: displayValue(online[field]),
      }));
  }, [document, resolveLabel]);

  const handleClose = () => {
    setConfirmingDiscard(false);
    onClose();
  };

  const handleDiscard = async () => {
    if (!document?.id) return;
    setWorking(true);
    try {
      await discardLockedOnlineConflict(document.id);
      handleClose();
    } finally {
      setWorking(false);
    }
  };

  const handleCreateNew = async () => {
    if (!document) return;
    setWorking(true);
    try {
      await onCreateNewDocument(document);
    } finally {
      setWorking(false);
    }
  };

  if (!document) return null;

  const copy = lockedOnlineCopy(document);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={copy.modalTitle}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">{copy.modalBody}</p>

        <div className="border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Field</th>
                <th className="px-3 py-2 text-left font-medium">Your edit</th>
                <th className="px-3 py-2 text-left font-medium">{copy.onlineColumn}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {changes.map(change => (
                <tr key={change.field}>
                  <td className="px-3 py-2 text-gray-700">{change.label}</td>
                  <td className="px-3 py-2 text-gray-900">{change.localValue}</td>
                  <td className="px-3 py-2 text-gray-500">{change.onlineValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {confirmingDiscard ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-3">
            <p className="text-sm text-red-800">
              Discarding permanently removes your offline edits to this document and cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmingDiscard(false)} disabled={working}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDiscard} disabled={working}>
                Discard edits
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setConfirmingDiscard(true)} disabled={working}>
              Discard edits
            </Button>
            <Button variant="primary" onClick={handleCreateNew} disabled={working}>
              Create new document from these edits
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
